import { supabase } from '@/utils/supabase';
import {
  buildPickupComparisonDetails,
  buildPickupObComparisonInput,
  derivePickupVerificationPresentation,
  PICKUP_CANONICAL_ENCOMPASS_INPUT_COLUMNS,
  type PickupComparisonView,
  type PickupCanonicalLoanInput,
  type PickupFieldComparison,
  type PickupObCacheComparisonSource
} from './pickupReconciliationPolicy';
import type { PickupVerificationStatus } from './types';
import type { PickupInvestorPricingContext } from './pickupInvestorPricing';
import {
  PICKUP_LOAN_CENTER_LABEL,
  PICKUP_OPTIMAL_BLUE_LABEL
} from './pickupVerificationPresentation';

export const PICKUP_ENCOMPASS_COMPARISON_SELECT = [
  ...PICKUP_CANONICAL_ENCOMPASS_INPUT_COLUMNS,
  'trans_details_loan_number',
  'borrower_first_name',
  'borrower_last_name'
].join(', ');

const CACHE_COMPARISON_COLUMNS = [
  'id',
  'ob_loan_id',
  'los_loan_number',
  'encompass_loan_id',
  'borrower_name',
  'raw_loan_payload',
  'raw_product_payload',
  'expanded_guidelines',
  'investor_name',
  'product_name',
  'locked_rate',
  'locked_price',
  'lock_period',
  'lock_expiration_date',
  'base_loan_amount',
  'loan_purpose',
  'loan_type',
  'lien_type',
  'representative_fico',
  'dti',
  'ltv',
  'cltv',
  'hcltv',
  'sales_price',
  'appraised_value',
  'property_type',
  'occupancy',
  'property_state',
  'property_county',
  'property_zip',
  'property_number_of_units',
  'pickup_verification_status',
  'pickup_verification_attempt_count',
  'pickup_verification_retry_after',
  'pickup_verification_last_error',
  'pickup_last_verified_at'
].join(', ');

export interface PickupDriftInspectorTarget {
  encompassLoanId?: string | null;
  obLoanCacheId?: string | null;
  obLoanId?: number | null;
  loanNumber?: string | null;
  borrowerName?: string | null;
  pricing?: PickupInvestorPricingContext | null;
}

interface PickupDriftEncompassRow extends PickupCanonicalLoanInput {
  encompass_loan_id: string;
  trans_details_loan_number: string | null;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
}

interface PickupDriftCacheRow extends PickupObCacheComparisonSource {
  id: string;
  ob_loan_id: number;
  los_loan_number: string | null;
  encompass_loan_id: string | null;
  borrower_name: string | null;
  pickup_verification_status: PickupVerificationStatus | null;
  pickup_verification_attempt_count: number | null;
  pickup_verification_retry_after: string | null;
  pickup_verification_last_error: string | null;
  pickup_last_verified_at: string | null;
}

export interface PickupDriftInspectorData {
  loanNumber: string;
  borrowerName: string;
  verificationStatus: PickupVerificationStatus | null;
  lastVerifiedAt: string | null;
  attemptCount: number;
  retryAfter: string | null;
  lastError: string | null;
  comparisons: PickupFieldComparison[];
  blockingCount: number;
  minorCount: number;
  missingInObCount: number;
  defaultView: PickupComparisonView;
  consequence: string;
  hasObRecord: boolean;
  investorCommitment: string | null;
}

export interface PickupReverifyResult {
  success: true;
  obLoanId: number;
  verificationStatus: PickupVerificationStatus;
  blockingCount: number;
  minorCount: number;
  missingInObCount: number;
  message: string;
}

async function fetchEncompassComparisonRow(
  target: PickupDriftInspectorTarget
): Promise<PickupDriftEncompassRow> {
  let query = supabase.from('encompass_loans').select(PICKUP_ENCOMPASS_COMPARISON_SELECT);
  if (target.encompassLoanId) {
    query = query.eq('encompass_loan_id', target.encompassLoanId);
  } else if (target.loanNumber) {
    query = query.eq('trans_details_loan_number', target.loanNumber);
  } else {
    throw new Error(`This loan has no ${PICKUP_LOAN_CENTER_LABEL} identifier.`);
  }
  const { data, error } = await query.limit(1).maybeSingle();
  if (error) {
    throw new Error(`Could not load the ${PICKUP_LOAN_CENTER_LABEL} comparison: ${error.message}`);
  }
  if (!data) {
    throw new Error(`The ${PICKUP_LOAN_CENTER_LABEL} loan record could not be found.`);
  }
  return data as unknown as PickupDriftEncompassRow;
}

async function fetchObComparisonRow(
  target: PickupDriftInspectorTarget
): Promise<PickupDriftCacheRow | null> {
  let query = supabase.from('ob_loan_cache').select(CACHE_COMPARISON_COLUMNS);
  if (target.obLoanCacheId) {
    query = query.eq('id', target.obLoanCacheId);
  } else if (target.obLoanId) {
    query = query.eq('ob_loan_id', target.obLoanId);
  } else if (target.encompassLoanId) {
    query = query.eq('encompass_loan_id', target.encompassLoanId);
  } else if (target.loanNumber) {
    query = query.eq('los_loan_number', target.loanNumber);
  } else {
    return null;
  }
  const { data, error } = await query.limit(1).maybeSingle();
  if (error) {
    throw new Error(`Could not load the ${PICKUP_OPTIMAL_BLUE_LABEL} comparison: ${error.message}`);
  }
  return data as unknown as PickupDriftCacheRow | null;
}

export async function fetchPickupDriftInspectorData(
  target: PickupDriftInspectorTarget
): Promise<PickupDriftInspectorData> {
  const [encompass, cache] = await Promise.all([
    fetchEncompassComparisonRow(target),
    fetchObComparisonRow(target)
  ]);
  const borrowerName = [encompass.borrower_first_name, encompass.borrower_last_name]
    .filter(Boolean)
    .join(' ')
    || cache?.borrower_name
    || target.borrowerName
    || 'Borrower unavailable';
  const comparisons = cache
    ? buildPickupComparisonDetails(encompass, buildPickupObComparisonInput(cache))
    : [];
  const presentation = derivePickupVerificationPresentation({
    comparisons,
    currentStatus: cache?.pickup_verification_status,
    attemptCount: cache?.pickup_verification_attempt_count
  });
  const commitmentNumber = String(encompass.rate_lock_investor_commitment ?? '').trim();
  const commitmentInvestor = String(encompass.investor_name ?? '').trim();

  return {
    loanNumber: encompass.trans_details_loan_number || cache?.los_loan_number || target.loanNumber || 'Unknown loan',
    borrowerName,
    verificationStatus: cache ? presentation.status : null,
    lastVerifiedAt: cache?.pickup_last_verified_at ?? null,
    attemptCount: cache?.pickup_verification_attempt_count ?? 0,
    retryAfter: cache?.pickup_verification_retry_after ?? null,
    lastError: cache?.pickup_verification_last_error ?? null,
    comparisons,
    blockingCount: presentation.blocking.length,
    minorCount: presentation.minor.length,
    missingInObCount: presentation.missingInOb.length,
    defaultView: presentation.defaultView,
    consequence: presentation.consequence,
    hasObRecord: Boolean(cache),
    investorCommitment: commitmentNumber && commitmentInvestor
      ? `Committed to ${commitmentInvestor} · #${commitmentNumber}`
      : null
  };
}

export async function reverifyPickupLoan(obLoanId: number): Promise<PickupReverifyResult> {
  const { data, error } = await supabase.functions.invoke('ob-pickup-analysis', {
    body: { action: 'reverifyLoan', obLoanId }
  });
  if (error) {
    const context = (error as { context?: Response }).context;
    if (context) {
      try {
        const body = await context.clone().json() as { error?: string };
        throw new Error(body.error || 'OB re-verification failed.');
      } catch (parseError) {
        if (parseError instanceof Error && parseError.message !== 'OB re-verification failed.') {
          throw parseError;
        }
      }
    }
    throw new Error(error.message || 'OB re-verification failed.');
  }
  return data as PickupReverifyResult;
}
